import { Fragment, useMemo, useState } from 'react';
import type { AgentRunSummary, TaskPriority, TaskResponse } from '@erp/rpc';
import {
  ListChecksIcon,
  PlusIcon,
  SidebarSimpleIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  useBulkCancelAgentTasks,
  useBulkDeleteAgentTasks,
  useBulkEditDescriptionAgentTasks,
  useBulkSetPriorityAgentTasks,
  useCreateAgentTask,
  useDeleteAgentTask,
  useListAgentTasks,
  useResetAgentTaskStatus,
  useUpdateAgentTask,
} from 'src/api/rpc/hooks/tasks/useTasks';
import { useUsersInOrg } from 'src/api/rpc/hooks/core/useUsersInOrg';
import { cn } from 'src/lib/utils';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Card, CardContent } from 'src/components/ui/card';
import { Checkbox } from 'src/components/ui/checkbox';
import { useToast } from 'src/context/ToastContext';
import { BulkActionsBar } from './task-panel/BulkActionsBar';
import { BulkCancelDialog } from './task-panel/BulkCancelDialog';
import { partitionByTransition } from './task-panel/bulkTaskSelection';
import { BulkEditDescriptionDialog } from './task-panel/BulkEditDescriptionDialog';
import { InspectedTaskRail } from './task-panel/InspectedTaskRail';
import { OpenFullTaskButton } from './task-panel/OpenFullTaskButton';
import {
  TaskCreateDialog,
  type TaskCreateSubmit,
} from './task-panel/TaskCreateDialog';
import {
  TaskEditDialog,
  type TaskEditState,
} from './task-panel/TaskEditDialog';
import { TaskCard } from './task-panel/TaskCard';
import {
  inspectedIdAfterBulkDelete,
  inspectedIdAfterDelete,
  nextInspectedTaskId,
} from './task-panel/inspectTaskId';

type TaskTab = 'queue' | 'needs_input' | 'done';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

function skippedSuffix(skipped: number) {
  return skipped > 0 ? ` (${skipped} skipped)` : '';
}

function normalizeDescription(description: string) {
  const trimmed = description.trim();
  return trimmed.length > 0 ? description : null;
}

interface AgentMonitorTaskPanelProps {
  agentDefinitionId: string;
  latestRun: AgentRunSummary | null;
  onCollapse?: () => void;
  /**
   * When true (the default), a running task scrolls itself into view.
   * Agent pages turn this off so a live task at the bottom cannot yank
   * the window away from the run history.
   */
  autoScrollOnRunning?: boolean;
}

// Existing queue panel: create/edit/bulk handlers were already here. Inspect
// adds one piece of state and a sibling rail — not a new bind/assemble layer.
// fallow-ignore-next-line complexity
export function AgentMonitorTaskPanel({
  agentDefinitionId,
  latestRun,
  onCollapse,
  autoScrollOnRunning = true,
}: AgentMonitorTaskPanelProps) {
  const gt = useGT();
  const { grouped, isLoading } = useListAgentTasks(agentDefinitionId);
  const { data: usersData } = useUsersInOrg();
  const usersById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of usersData ?? []) {
      map.set(user.id, user.name);
    }
    return map;
  }, [usersData]);
  const createTaskMutation = useCreateAgentTask({ agentDefinitionId });
  const updateTaskMutation = useUpdateAgentTask({ agentDefinitionId });
  const deleteTaskMutation = useDeleteAgentTask({ agentDefinitionId });
  const resetTaskStatusMutation = useResetAgentTaskStatus({
    agentDefinitionId,
  });
  const bulkDeleteMutation = useBulkDeleteAgentTasks({ agentDefinitionId });
  const bulkCancelMutation = useBulkCancelAgentTasks({ agentDefinitionId });
  const bulkSetPriorityMutation = useBulkSetPriorityAgentTasks({
    agentDefinitionId,
  });
  const bulkEditDescriptionMutation = useBulkEditDescriptionAgentTasks({
    agentDefinitionId,
  });
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<TaskTab>('queue');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkEditDescOpen, setIsBulkEditDescOpen] = useState(false);
  const [isBulkCancelOpen, setIsBulkCancelOpen] = useState(false);
  const [editState, setEditState] = useState<TaskEditState | null>(null);
  const [busyDeleteTaskId, setBusyDeleteTaskId] = useState<string | null>(null);
  const [busyResetTaskId, setBusyResetTaskId] = useState<string | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [inspectedTaskId, setInspectedTaskId] = useState<string | null>(null);

  const isRunActive = latestRun?.status === 'running';
  const runningTaskId = useMemo(() => {
    if (!isRunActive || grouped.active.length === 0) return null;
    return (
      [...grouped.active].sort((a, b) => {
        const aTime = a.startedAt ? new Date(a.startedAt).getTime() : 0;
        const bTime = b.startedAt ? new Date(b.startedAt).getTime() : 0;
        return bTime - aTime;
      })[0]?.id ?? null
    );
  }, [isRunActive, grouped.active]);

  const recentDoneTasks = useMemo(() => {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return [...grouped.failed, ...grouped.completed, ...grouped.cancelled]
      .filter((t) => new Date(t.updatedAt).getTime() >= oneWeekAgo)
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
  }, [grouped.failed, grouped.completed, grouped.cancelled]);

  const tabCounts = {
    queue: grouped.active.length + grouped.queue.length,
    needs_input: grouped.needsInput.length,
    done: recentDoneTasks.length,
  };
  const totalTasks = tabCounts.queue + tabCounts.needs_input + tabCounts.done;

  const tabTasks = useMemo((): TaskResponse[] => {
    switch (activeTab) {
      case 'queue':
        return [...grouped.active, ...grouped.queue];
      case 'needs_input':
        return grouped.needsInput;
      case 'done':
        return recentDoneTasks;
    }
  }, [
    activeTab,
    grouped.active,
    grouped.queue,
    grouped.needsInput,
    recentDoneTasks,
  ]);

  // Bulk actions act only on the selected tasks in the open tab, so a task
  // that refetched into another tab can't ride along unseen.
  const selectedTasks = tabTasks.filter((t) => selectedTaskIds.has(t.id));
  const allSelected =
    tabTasks.length > 0 && selectedTasks.length === tabTasks.length;
  const someSelected = selectedTasks.length > 0;

  function handleSelectAll(select: boolean) {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      for (const t of tabTasks) {
        if (select) next.add(t.id);
        else next.delete(t.id);
      }
      return next;
    });
  }

  function handleInspect(task: TaskResponse) {
    setInspectedTaskId((current) => nextInspectedTaskId(current, task.id));
  }

  function handleEdit(task: TaskResponse) {
    setEditState({
      taskId: task.id,
      title: task.title,
      description: task.description ?? '',
      metadata: task.metadata ?? null,
      readOnly: TERMINAL_STATUSES.has(task.status),
    });
  }

  function handleSelectedChange(task: TaskResponse, selected: boolean) {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (selected) next.add(task.id);
      else next.delete(task.id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedTaskIds(new Set());
  }

  async function handleCreateTask(values: TaskCreateSubmit) {
    try {
      await createTaskMutation.mutateAsync({
        title: values.title,
        description: values.description,
        priority: values.priority,
        assigneeType: 'agent',
        assigneeAgentDefinitionId: agentDefinitionId,
      });
      setIsCreateOpen(false);
      showToast({ title: 'Task created', color: 'success' });
    } catch {
      showToast({ title: 'Failed to create task', color: 'error' });
    }
  }

  async function handleSaveTask() {
    if (!editState) return;
    try {
      await updateTaskMutation.mutateAsync({
        id: editState.taskId,
        title: editState.title.trim(),
        description: normalizeDescription(editState.description),
      });
      setEditState(null);
      showToast({ title: 'Task updated', color: 'success' });
    } catch {
      showToast({ title: 'Failed to update task', color: 'error' });
    }
  }

  async function handleDeleteTask(task: TaskResponse) {
    setBusyDeleteTaskId(task.id);
    try {
      await deleteTaskMutation.mutateAsync({ id: task.id });
      setInspectedTaskId((current) => inspectedIdAfterDelete(current, task.id));
      showToast({ title: 'Task deleted', color: 'success' });
    } catch {
      showToast({ title: 'Failed to delete task', color: 'error' });
    } finally {
      setBusyDeleteTaskId(null);
    }
  }

  async function handleResetTaskStatus(task: TaskResponse) {
    setBusyResetTaskId(task.id);
    try {
      await resetTaskStatusMutation.mutateAsync({ taskIds: [task.id] });
      showToast({
        title: 'Task status reset',
        message: `${task.title} is back in the queue.`,
        color: 'success',
      });
    } catch {
      showToast({ title: 'Failed to reset task status', color: 'error' });
    } finally {
      setBusyResetTaskId(null);
    }
  }

  function handleTabChange(tab: TaskTab) {
    setActiveTab(tab);
    clearSelection();
  }

  async function handleBulkReset() {
    const { ids, skipped: skippedCount } = partitionByTransition(
      selectedTasks,
      'pending',
    );
    const skipped = skippedSuffix(skippedCount);
    if (ids.length === 0) {
      showToast({
        title: `None of the selected tasks can be reset${skipped}`,
        color: 'warning',
      });
      return;
    }
    try {
      await resetTaskStatusMutation.mutateAsync({ taskIds: ids });
      clearSelection();
      showToast({
        title: `${ids.length} tasks reset${skipped}`,
        color: 'success',
      });
    } catch {
      showToast({ title: 'Failed to reset tasks', color: 'error' });
    }
  }

  async function handleBulkCancel(reason: string) {
    const { ids, skipped: skippedCount } = partitionByTransition(
      selectedTasks,
      'cancelled',
    );
    const skipped = skippedSuffix(skippedCount);
    if (ids.length === 0) {
      setIsBulkCancelOpen(false);
      showToast({
        title: `None of the selected tasks can be cancelled${skipped}`,
        color: 'warning',
      });
      return;
    }
    try {
      await bulkCancelMutation.mutateAsync({ ids, reason });
      setIsBulkCancelOpen(false);
      clearSelection();
      showToast({
        title: `${ids.length} tasks cancelled${skipped}`,
        color: 'success',
      });
    } catch (err) {
      showToast({
        title: err instanceof Error ? err.message : 'Failed to cancel tasks',
        color: 'error',
      });
    }
  }

  async function handleBulkSetPriority(priority: TaskPriority) {
    const ids = selectedTasks.map((task) => task.id);
    if (ids.length === 0) return;
    try {
      await bulkSetPriorityMutation.mutateAsync({ ids, priority });
      clearSelection();
      showToast({
        title: `${ids.length} tasks updated`,
        color: 'success',
      });
    } catch {
      showToast({ title: 'Failed to update tasks', color: 'error' });
    }
  }

  async function handleBulkDelete() {
    const ids = selectedTasks.map((task) => task.id);
    if (ids.length === 0) return;
    const attempted = new Set(ids);
    try {
      await bulkDeleteMutation.mutateAsync({ ids });
      clearSelection();
      showToast({
        title: `${ids.length} tasks deleted`,
        color: 'success',
      });
    } catch {
      showToast({ title: 'Failed to delete tasks', color: 'error' });
    } finally {
      // allSettled can delete some ids and still reject. Close the rail if
      // the inspected task was in the batch so it cannot point at a gone row.
      setInspectedTaskId((current) =>
        inspectedIdAfterBulkDelete(current, attempted),
      );
    }
  }

  async function handleBulkEditDescription(description: string | null) {
    const ids = selectedTasks.map((task) => task.id);
    if (ids.length === 0) return;
    try {
      await bulkEditDescriptionMutation.mutateAsync({ ids, description });
      setIsBulkEditDescOpen(false);
      clearSelection();
      showToast({
        title: `${ids.length} task descriptions updated`,
        color: 'success',
      });
    } catch {
      showToast({
        title: 'Failed to update descriptions',
        color: 'error',
      });
    }
  }

  const tabs: { key: TaskTab; label: string }[] = [
    { key: 'queue', label: gt('Queue') },
    { key: 'needs_input', label: gt('Needs Input') },
    { key: 'done', label: gt('Done') },
  ];

  return (
    <>
      <Card className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-2 shrink-0">
          <ListChecksIcon className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1 text-sm font-semibold">
            <T context="agent monitor task panel heading">Tasks</T>
          </span>
          {totalTasks > 0 && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {totalTasks}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsCreateOpen(true)}
            aria-label={gt('Create task', {
              context: 'agent monitor create task button aria label',
            })}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
          {onCollapse && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onCollapse}
              aria-label={gt('Collapse task panel', {
                context: 'agent monitor collapse task panel button',
              })}
            >
              <SidebarSimpleIcon className="h-4 w-4" />
            </Button>
          )}
        </div>

        {totalTasks > 0 && (
          <div className="flex gap-1 px-3 pb-2 shrink-0">
            {tabs.map(({ key, label }) => {
              const count = tabCounts[key];
              const hasFailures = key === 'done' && grouped.failed.length > 0;
              const hasNeedsInput =
                key === 'needs_input' && grouped.needsInput.length > 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabChange(key)}
                  className={cn(
                    'flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md transition-colors',
                    activeTab === key
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                  )}
                >
                  {label}
                  {count > 0 && <span className="tabular-nums">{count}</span>}
                  {hasFailures && activeTab !== key && (
                    <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
                  )}
                  {hasNeedsInput && activeTab !== key && (
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        <CardContent className="pt-0 px-2 min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              <T context="Loading state for agent tasks">Loading tasks...</T>
            </div>
          ) : totalTasks === 0 ? (
            <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-muted-foreground">
              <T context="agent monitor empty task list">
                No tasks assigned to this agent.
              </T>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCreateOpen(true)}
              >
                <PlusIcon className="mr-1 h-4 w-4" />
                <T context="agent monitor create task button label">
                  Create task
                </T>
              </Button>
            </div>
          ) : tabTasks.length === 0 ? (
            <div className="py-4 text-center text-xs text-muted-foreground">
              {activeTab === 'queue' && (
                <T context="agent monitor empty queue">Queue is empty.</T>
              )}
              {activeTab === 'needs_input' && (
                <T context="agent monitor no needs input tasks">
                  No tasks waiting for input.
                </T>
              )}
              {activeTab === 'done' && (
                <T context="agent monitor no completed tasks">
                  No completed tasks yet.
                </T>
              )}
            </div>
          ) : (
            <div>
              {selectedTasks.length > 0 && (
                <BulkActionsBar
                  selectedCount={selectedTasks.length}
                  onClear={clearSelection}
                  onReset={() => {
                    void handleBulkReset();
                  }}
                  onCancel={() => setIsBulkCancelOpen(true)}
                  onDelete={() => {
                    void handleBulkDelete();
                  }}
                  onSetPriority={(priority) => {
                    void handleBulkSetPriority(priority);
                  }}
                  onEditDescription={() => setIsBulkEditDescOpen(true)}
                  isResetting={resetTaskStatusMutation.isPending}
                  isCancelling={bulkCancelMutation.isPending}
                  isDeleting={bulkDeleteMutation.isPending}
                  isUpdatingPriority={bulkSetPriorityMutation.isPending}
                  isEditingDescription={bulkEditDescriptionMutation.isPending}
                />
              )}

              {tabTasks.length > 1 && (
                <div className="flex items-center gap-2 px-2 py-1">
                  <Checkbox
                    checked={
                      allSelected
                        ? true
                        : someSelected
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={(value) => handleSelectAll(value === true)}
                    aria-label={gt('Select all tasks in this tab')}
                    className="h-3 w-3"
                  />
                  <span className="text-[11px] text-muted-foreground">
                    <T context="select all tasks label">Select all</T>
                  </span>
                </div>
              )}

              <div className="space-y-0.5">
                {tabTasks.map((task) => {
                  const inspected = inspectedTaskId === task.id;
                  return (
                    <Fragment key={task.id}>
                      <TaskCard
                        task={task}
                        onInspect={handleInspect}
                        onEdit={handleEdit}
                        onDelete={handleDeleteTask}
                        onResetStatus={handleResetTaskStatus}
                        isDeleting={busyDeleteTaskId === task.id}
                        isResetting={busyResetTaskId === task.id}
                        selected={selectedTaskIds.has(task.id)}
                        onSelectedChange={handleSelectedChange}
                        active={inspected}
                        isRunning={runningTaskId === task.id}
                        autoScrollOnRunning={autoScrollOnRunning}
                        assigneeName={
                          task.assigneeUserId
                            ? (usersById.get(task.assigneeUserId) ?? null)
                            : null
                        }
                        showOpenFullTask
                      />
                      {inspected && (
                        // Rendered inside the scrolling list, directly under
                        // its row, so the review stays attached while the
                        // queue scrolls instead of sitting in a fixed strip.
                        <InspectedTaskRail
                          taskId={task.id}
                          trailing={<OpenFullTaskButton taskId={task.id} />}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <TaskCreateDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={handleCreateTask}
        isSubmitting={createTaskMutation.isPending}
      />

      <TaskEditDialog
        editState={editState}
        onOpenChange={(open) => {
          if (!open) setEditState(null);
        }}
        onTitleChange={(title) => {
          setEditState((current) =>
            current ? { ...current, title } : current,
          );
        }}
        onDescriptionChange={(description) => {
          setEditState((current) =>
            current ? { ...current, description } : current,
          );
        }}
        onSave={handleSaveTask}
        isSaving={updateTaskMutation.isPending}
      />

      <BulkEditDescriptionDialog
        open={isBulkEditDescOpen}
        onOpenChange={setIsBulkEditDescOpen}
        selectedCount={selectedTasks.length}
        onSubmit={handleBulkEditDescription}
        isSubmitting={bulkEditDescriptionMutation.isPending}
      />
      <BulkCancelDialog
        open={isBulkCancelOpen}
        onOpenChange={setIsBulkCancelOpen}
        selectedCount={selectedTasks.length}
        onSubmit={handleBulkCancel}
        isSubmitting={bulkCancelMutation.isPending}
      />
    </>
  );
}
